/**
 * StratIQX Service Configuration
 * Central source of truth for all service definitions
 */

import { 
  ServiceConfig,
  ServiceSpecifications,
  CustomerBenefits,
  DetailLevel
} from './types/service.types';

export const SERVICES_CONFIG: Record<string, ServiceConfig> = {
  'board-executive-brief': {
    id: 'board-executive-brief',
    name: 'Board Executive Brief',
    description: 'Ultra-concise strategic intelligence for board-level decision making. Our elite AI orchestrator delivers the most critical insights from $100,000+ Big 3 consulting engagements, distilled into a decisive 3-5 page board briefing, delivered within 4 hours.',
    price: {
      value: 49,
      currency: 'USD',
      display: '$49.00 USD'
    },
    deposit: {
      type: 'none',
      value: 0,
      amount: 0,
      display: 'No upfront payment required'
    },
    billingType: 'one-time',
    features: [
      'Ultra-condensed 3-5 page board briefing',
      'Board Summary: Single strategic challenge with top 3 initiatives',
      'Strategic Imperatives: Exactly 3 action-forcing decisions',
      'Binary go/no-go decision framing with specific investments',
      'Top 3 quantified outcomes only (revenue, cost, market)',
      'LaTeX-generated PDF with professional typesetting',
      'Chicago style formatting for executive presentation',
      'Zero operational details - strategic decisions only',
      'Delivered within 4 hours via email (20-minute AI processing)'
    ],
    metadata: {
      deliveryTime: '4 hours',
      reportPages: '3-5',
      targetRevenue: '$50M+',
      includesPresentation: false,
      detailLevel: 'executiveBrief' as DetailLevel,
      specifications: {
        documents: {
          pageCount: {
            min: 3,
            max: 5,
            display: '3-5 pages'
          },
          formats: {
            pdf: true,
            html: false,
            json: false,
            powerpoint: false
          },
          formatDescriptions: {
            pdf: 'LaTeX-generated PDF with professional typesetting, Chicago style formatting, and executive presentation quality'
          }
        },
        processing: {
          maxTokensPerSection: 2000,
          totalSections: 2,
          aiModel: 'llama-4-scout-17b-16e-instruct',
          processingTime: '20 minutes'
        },
        sections: {
          included: [
            'Board Summary',
            'Strategic Imperatives'
          ],
          sectionDetails: {
            'Board Summary': {
              description: 'Single strategic challenge, top 3 initiatives with investments, top 3 quantified outcomes, board approval decision',
              typicalPages: 2,
              tokenAllocation: 2000
            },
            'Strategic Imperatives': {
              description: 'Exactly 3 action-forcing imperatives with Why Now, Expected Outcome, Decision Required, and Timeline (quarter-level)',
              typicalPages: 2,
              tokenAllocation: 2000
            }
          }
        },
        visualAssets: {
          charts: {
            count: 0,
            types: [],
            interactive: false
          },
          infographics: false,
          presentation: null
        },
        quality: {
          consultingValue: '$100,000+',
          completionTime: '4 hours',
          qualityScore: 98
        }
      } as ServiceSpecifications,
      customerBenefits: {
        primaryValue: 'Board-ready strategic intelligence worth $100,000+ distilled to critical decisions only',
        keyDeliverables: [
          'Ultra-condensed 3-5 page board briefing',
          'Top 3 strategic initiatives with specific investment amounts',
          'Exactly 3 action-forcing imperatives with binary decisions',
          'Top 3 quantified outcomes (revenue, cost, market)',
          'LaTeX-generated PDF with professional typesetting',
          'Chicago style formatting for executive credibility',
          'Zero operational details or supporting analysis'
        ],
        bestFor: 'Board meetings, governance decisions, capital allocation, strategic approvals, urgent C-suite decisions',
        notRecommendedFor: 'Detailed analysis, implementation planning, or operational reviews (see other service tiers)'
      } as CustomerBenefits
    },
    icon: 'Crown',
    headerColor: 'from-purple-900 to-indigo-900',
    popular: false,
    active: true,
    comingSoon: false,
    displayOrder: 0,
    /**
     * ⚠️ CRITICAL HANDSHAKE: tierLevel maps to backend analysisDepth via AdminOrchestratorService
     * Backend mapping: 'board-executive-brief' → DEPTH_CONFIGURATIONS['executiveBrief']
     * @see ai-consulting-main-worker/src/handlers/admin/services/AdminOrchestratorService.js:520
     * DO NOT change without verifying backend mapper includes this key
     */
    tierLevel: 'board-executive-brief'  // Service ID - backend maps to 'executiveBrief' depth
  },

  'market-essentials': {
    id: 'market-essentials',
    name: 'Market Essentials',
    description: 'Professional strategic intelligence that replaces $25,000+ consulting engagements. Our AI orchestrator delivers comprehensive market analysis with the depth and rigor of traditional consulting firms, delivered within 4 hours instead of 3 months.',
    price: {
      value: 149,
      currency: 'USD',
      display: '$149.00 USD'
    },
    deposit: {
      type: 'none',
      value: 0,
      amount: 0,
      display: 'No upfront payment required'
    },
    billingType: 'one-time',
    features: [
      'Professional 20-25 page strategic analysis',
      'AI-powered competitive intelligence and market positioning',
      'Operational and financial analysis with ROI projections',
      'Strategic recommendations with implementation timeline',
      'LaTeX-generated PDF with professional typesetting',
      'Chicago style formatting for professional credibility',
      'Delivered within 4 hours via email (30-minute AI processing)'
    ],
    metadata: {
      deliveryTime: '4 hours',
      reportPages: '20-25',
      targetRevenue: '$1M-$5M',
      detailLevel: 'standard' as DetailLevel,
      specifications: {
        documents: {
          pageCount: {
            min: 20,
            max: 25,
            display: '20-25 pages'
          },
          formats: {
            pdf: true,
            html: false,
            json: false,
            powerpoint: false
          },
          formatDescriptions: {
            pdf: 'LaTeX-generated PDF with professional typesetting, Chicago style formatting, and comprehensive strategic analysis'
          }
        },
        processing: {
          maxTokensPerSection: 2200,
          totalSections: 5,
          aiModel: 'llama-4-scout-17b-16e-instruct',
          processingTime: '30 minutes'
        },
        sections: {
          included: [
            'Executive Summary',
            'Operational Analysis',
            'Financial Analysis',
            'Strategic Recommendations',
            'Conclusion and Next Steps'
          ],
          sectionDetails: {
            'Executive Summary': {
              description: 'C-suite ready overview with key insights and immediate actions',
              typicalPages: 3.5,
              tokenAllocation: 2200
            },
            'Operational Analysis': {
              description: 'Deep dive into operational efficiency and process optimization',
              typicalPages: 5.5,
              tokenAllocation: 2200
            },
            'Financial Analysis': {
              description: 'Revenue opportunities, cost optimization, and financial projections',
              typicalPages: 5.5,
              tokenAllocation: 2200
            },
            'Strategic Recommendations': {
              description: 'Prioritized action plan with implementation timeline',
              typicalPages: 6.5,
              tokenAllocation: 2200
            },
            'Conclusion and Next Steps': {
              description: 'Summary of key decisions and action items',
              typicalPages: 1.5,
              tokenAllocation: 2200
            }
          }
        },
        visualAssets: {
          charts: {
            count: 0,
            types: [],
            interactive: false
          },
          infographics: false,
          presentation: null
        },
        quality: {
          consultingValue: '$25,000',
          completionTime: '4 hours',
          qualityScore: 85
        }
      } as ServiceSpecifications,
      customerBenefits: {
        primaryValue: 'Get $25,000 worth of strategic consulting delivered in 4 hours',
        keyDeliverables: [
          'Comprehensive 20-25 page strategic report',
          '5 detailed analysis sections with actionable insights',
          'LaTeX-generated PDF with professional typesetting',
          'Chicago style formatting for professional credibility',
          'Implementation roadmap with timeline'
        ],
        bestFor: 'Quarterly planning, strategic pivots, investor preparation, operational improvements',
        notRecommendedFor: 'Quick competitive checks (see Quick Insights) or enterprise transformations (see Pro/Enterprise)'
      } as CustomerBenefits
    },
    icon: 'TrendingUp',
    headerColor: 'from-blue-600 to-cyan-600',
    popular: false,
    active: true,
    comingSoon: false,
    tierLevel: 'standard',
    displayOrder: 1
  },

  'market-intelligence': {
    id: 'market-intelligence',
    name: 'Market Intelligence Pro',
    description: 'Deep strategic intelligence for complex business decisions. Our advanced AI orchestrator provides the analytical depth of $50,000+ consulting projects, including PowerPoint presentations and comprehensive market intelligence, delivered within 4 hours.',
    price: {
      value: 299,
      currency: 'USD',
      display: '$299.00 USD'
    },
    deposit: {
      type: 'none',
      value: 0,
      amount: 0,
      display: 'No upfront payment required'
    },
    billingType: 'one-time',
    features: [
      'Comprehensive 25-35 page strategic intelligence report',
      '6 complete analysis sections with deep insights',
      'Advanced market intelligence and competitive positioning',
      'Financial modeling with scenario analysis',
      'LaTeX-generated PDF with professional typesetting',
      'Chicago style formatting for executive presentation',
      'Implementation roadmap with KPIs and milestones',
      'Delivered within 4 hours via email (45-minute AI processing)'
    ],
    metadata: {
      deliveryTime: '4 hours',
      reportPages: '25-35',
      targetRevenue: '$5M-$20M',
      detailLevel: 'comprehensive' as DetailLevel,
      specifications: {
        documents: {
          pageCount: {
            min: 25,
            max: 35,
            display: '25-35 pages'
          },
          formats: {
            pdf: true,
            html: false,
            json: false,
            powerpoint: false
          },
          formatDescriptions: {
            pdf: 'LaTeX-generated PDF with professional typesetting, Chicago style formatting, and comprehensive strategic intelligence'
          }
        },
        processing: {
          maxTokensPerSection: 2048,
          totalSections: 6,
          aiModel: 'Llama 3.1 8B Instruct',
          processingTime: '45 minutes'
        },
        sections: {
          included: [
            'Executive Summary',
            'Operational Analysis',
            'Financial Analysis',
            'Strategic Recommendations',
            'Market Analysis',
            'Implementation Roadmap'
          ],
          sectionDetails: {
            'Executive Summary': {
              description: 'Board-ready synthesis with strategic imperatives',
              typicalPages: 3,
              tokenAllocation: 500
            },
            'Operational Analysis': {
              description: 'Comprehensive operational assessment with efficiency metrics',
              typicalPages: 6,
              tokenAllocation: 2048
            },
            'Financial Analysis': {
              description: 'Advanced financial modeling with scenario analysis',
              typicalPages: 6,
              tokenAllocation: 2048
            },
            'Strategic Recommendations': {
              description: 'Detailed strategic initiatives with ROI projections',
              typicalPages: 5,
              tokenAllocation: 2048
            },
            'Market Analysis': {
              description: 'Deep market intelligence with competitive positioning',
              typicalPages: 5,
              tokenAllocation: 2048
            },
            'Implementation Roadmap': {
              description: 'Phased execution plan with milestones and KPIs',
              typicalPages: 4,
              tokenAllocation: 2048
            }
          }
        },
        visualAssets: {
          charts: {
            count: 0,
            types: [],
            interactive: false
          },
          infographics: false,
          presentation: null
        },
        quality: {
          consultingValue: '$50,000',
          completionTime: '4 hours',
          qualityScore: 92
        }
      } as ServiceSpecifications,
      customerBenefits: {
        primaryValue: 'Enterprise-grade strategic intelligence worth $50,000+ delivered in 4 hours',
        keyDeliverables: [
          'Comprehensive 25-35 page strategic intelligence report',
          '6 in-depth analysis sections with advanced insights',
          'LaTeX-generated PDF with professional typesetting',
          'Chicago style formatting for executive presentation',
          'Implementation roadmap with KPIs'
        ],
        bestFor: 'Annual planning, major strategic decisions, board presentations, investor meetings, M&A preparation',
        notRecommendedFor: 'Quick assessments (see Market Pulse/Essentials) or routine operational reviews'
      } as CustomerBenefits
    },
    icon: 'BarChart3',
    headerColor: 'from-purple-600 to-pink-600',
    popular: false,
    active: false,
    comingSoon: true,
    displayOrder: 2,
    tierLevel: 'comprehensive'
  },
  
  'strategic-analysis': {
    id: 'strategic-analysis',
    name: 'Enterprise Intelligence Suite',
    description: 'Board-ready strategic intelligence for the most critical business decisions. Our enterprise AI orchestrator delivers the analytical rigor of $75,000+ Big 3 consulting engagements, complete with board presentations and comprehensive risk assessments, delivered within 4 hours.',
    price: {
      value: 599,
      currency: 'USD',
      display: '$599.00 USD'
    },
    deposit: {
      type: 'none',
      value: 0,
      amount: 0,
      display: 'No upfront payment required'
    },
    billingType: 'one-time',
    features: [
      'Board-ready 40+ page strategic intelligence suite',
      '8 comprehensive analysis sections with C-suite insights',
      'Advanced risk assessment and mitigation strategies',
      'Global market expansion and M&A analysis',
      'LaTeX-generated PDF with professional typesetting',
      'Chicago style formatting for board-level presentation',
      'Advanced financial modeling and strategic analysis',
      'Delivered within 4 hours via email (60-minute AI processing)'
    ],
    metadata: {
      deliveryTime: '4 hours',
      reportPages: '40+',
      targetRevenue: '$20M+',
      includesPresentation: false,
      detailLevel: 'enterprise' as DetailLevel,
      specifications: {
        documents: {
          pageCount: {
            min: 40,
            max: 60,
            display: '40+ pages'
          },
          formats: {
            pdf: true,
            html: false,
            json: false,
            powerpoint: false
          },
          formatDescriptions: {
            pdf: 'LaTeX-generated PDF with professional typesetting, Chicago style formatting, and board-ready strategic intelligence'
          }
        },
        processing: {
          maxTokensPerSection: 2048,
          totalSections: 8,
          aiModel: 'Llama 3.1 8B Instruct',
          processingTime: '60+ minutes'
        },
        sections: {
          included: [
            'Executive Summary',
            'Operational Analysis',
            'Financial Analysis',
            'Strategic Recommendations',
            'Market Analysis',
            'Implementation Roadmap',
            'Risk Assessment',
            'Competitive Analysis'
          ],
          sectionDetails: {
            'Executive Summary': {
              description: 'Board-level executive briefing with strategic imperatives',
              typicalPages: 3,
              tokenAllocation: 500
            },
            'Operational Analysis': {
              description: 'Enterprise operational excellence assessment',
              typicalPages: 7,
              tokenAllocation: 2048
            },
            'Financial Analysis': {
              description: 'Enterprise financial modeling with M&A scenarios',
              typicalPages: 7,
              tokenAllocation: 2048
            },
            'Strategic Recommendations': {
              description: 'C-suite strategic initiatives with board-ready justification',
              typicalPages: 6,
              tokenAllocation: 2048
            },
            'Market Analysis': {
              description: 'Global market intelligence and expansion opportunities',
              typicalPages: 6,
              tokenAllocation: 2048
            },
            'Implementation Roadmap': {
              description: 'Enterprise transformation roadmap with change management',
              typicalPages: 5,
              tokenAllocation: 2048
            },
            'Risk Assessment': {
              description: 'Enterprise risk matrix with mitigation strategies',
              typicalPages: 4,
              tokenAllocation: 2048
            },
            'Competitive Analysis': {
              description: 'Deep competitive intelligence with strategic positioning',
              typicalPages: 4,
              tokenAllocation: 2048
            }
          }
        },
        visualAssets: {
          charts: {
            count: 0,
            types: [],
            interactive: false
          },
          infographics: false,
          presentation: null
        },
        quality: {
          consultingValue: '$75,000+',
          completionTime: '4 hours',
          qualityScore: 95
        }
      } as ServiceSpecifications,
      customerBenefits: {
        primaryValue: 'Board-ready strategic intelligence worth $75,000+ from Big 3 consulting firms',
        keyDeliverables: [
          'Comprehensive 40+ page enterprise report',
          '8 complete analysis sections with C-suite insights',
          'LaTeX-generated PDF with professional typesetting',
          'Chicago style formatting for board-level credibility',
          'Advanced financial modeling and risk assessment',
          'Priority delivery and white-glove service'
        ],
        bestFor: 'Board presentations, M&A decisions, enterprise transformation, market expansion, investor relations',
        notRecommendedFor: 'Routine analysis or quick assessments (see other service tiers)'
      } as CustomerBenefits
    },
    icon: 'Target',
    headerColor: 'from-amber-600 to-orange-600',
    popular: false,
    active: false,
    comingSoon: true,
    displayOrder: 3,
    tierLevel: 'enterprise'
  },

  'market-pulse-executive': {
    id: 'market-pulse-executive',
    name: 'Quick Insights',
    description: 'Fast, comprehensive market intelligence for time-sensitive decisions. Our AI orchestrator delivers professional strategic analysis with detailed insights and recommendations - perfect for quarterly planning and competitive assessments.',
    price: {
      value: 99,
      currency: 'USD',
      display: '$99.00 USD'
    },
    deposit: {
      type: 'none',
      amount: 0,
      display: 'No credit card required'
    },
    billingType: 'one-time',
    features: [
      'Comprehensive 8-12 page market intelligence report',
      'AI-powered competitive analysis and market positioning',
      'Strategic opportunity identification with ROI estimates',
      'Key findings and actionable strategic recommendations',
      'LaTeX-generated PDF with professional typesetting',
      'Chicago style formatting for professional credibility',
      'Delivered within 4 hours via email (15-minute AI processing)'
    ],
    metadata: {
      deliveryTime: '4 hours',
      reportPages: '8-12',
      includesPresentation: false,
      reportType: 'Quick Strategic Analysis',
      detailLevel: 'quick' as DetailLevel,
      specifications: {
        documents: {
          pageCount: {
            min: 8,
            max: 12,
            display: '8-12 pages'
          },
          formats: {
            pdf: true,
            html: false,
            json: false,
            powerpoint: false
          },
          formatDescriptions: {
            pdf: 'LaTeX-generated PDF with professional typesetting and Chicago style formatting'
          }
        },
        processing: {
          maxTokensPerSection: 1200,
          totalSections: 4,
          aiModel: 'llama-4-scout-17b-16e-instruct',
          processingTime: '15 minutes'
        },
        sections: {
          included: [
            'Executive Summary',
            'Key Findings',
            'Top Recommendations',
            'Conclusion and Next Steps'
          ],
          sectionDetails: {
            'Executive Summary': {
              description: 'High-level overview of market position and opportunities',
              typicalPages: 3.5,
              tokenAllocation: 1200
            },
            'Key Findings': {
              description: 'Critical insights from market analysis',
              typicalPages: 2.5,
              tokenAllocation: 1200
            },
            'Top Recommendations': {
              description: 'Actionable strategic recommendations with supporting details',
              typicalPages: 3.5,
              tokenAllocation: 1200
            },
            'Conclusion and Next Steps': {
              description: 'Summary and immediate action items',
              typicalPages: 0.75,
              tokenAllocation: 1200
            }
          }
        },
        visualAssets: {
          charts: {
            count: 0,
            types: [],
            interactive: false
          },
          infographics: false,
          presentation: null
        },
        quality: {
          consultingValue: '$10,000',
          completionTime: '4 hours',
          qualityScore: 75
        }
      } as ServiceSpecifications,
      customerBenefits: {
        primaryValue: 'Fast strategic intelligence for time-sensitive decisions',
        keyDeliverables: [
          'Comprehensive 8-12 page strategic report',
          '4 detailed analysis sections with AI insights',
          'LaTeX-generated PDF with professional typesetting',
          'Chicago style formatting for professional credibility',
          'Actionable recommendations with next steps'
        ],
        bestFor: 'Quick strategic assessments, competitive checks, quarterly planning reviews',
        notRecommendedFor: 'Comprehensive strategic planning or investor presentations (see Market Essentials or Pro tiers)'
      } as CustomerBenefits
    },
    icon: 'Zap',
    headerColor: 'from-green-600 to-emerald-600',
    popular: false,
    active: false,
    comingSoon: true,
    tierLevel: 'quick',
    displayOrder: 2  // Between Brief and Essentials
  }
};

/**
 * Get all service IDs
 */
export const SERVICE_IDS = Object.keys(SERVICES_CONFIG) as const;

/**
 * Service ID type for type safety
 */
export type ServiceId = keyof typeof SERVICES_CONFIG;

/**
 * Export individual services for convenience
 */
export const BOARD_EXECUTIVE_BRIEF = SERVICES_CONFIG['board-executive-brief'];
export const MARKET_INTELLIGENCE = SERVICES_CONFIG['market-intelligence'];
export const STRATEGIC_ANALYSIS = SERVICES_CONFIG['strategic-analysis'];
export const MARKET_ESSENTIALS = SERVICES_CONFIG['market-essentials'];
export const MARKET_PULSE_EXECUTIVE = SERVICES_CONFIG['market-pulse-executive'];